import {useEffect, useMemo, useState, useRef} from 'react'
import {useNavigate, useParams} from 'react-router-dom'
import BorderedCard from 'components/BorderedCard/BorderedCard'
import CheckedCircleOutlineIcon from 'assets/icons/CheckedCircleOutlineIcon'
import {useSelector} from 'react-redux'
import {RootState} from 'redux/store'

const INTRO_AUTO_NAVIGATE_DELAY_MS = 2000

const TreatmentStartedIntro = () => {
  const [countdown, setCountdown] = useState(Math.ceil(INTRO_AUTO_NAVIGATE_DELAY_MS / 1000))
  const navigate = useNavigate()
  const {patientId} = useParams()
  const timerRef = useRef<NodeJS.Timeout | null>(null)
  const hasNavigatedRef = useRef(false)
  const [isNavigating, setIsNavigating] = useState(false)

  const {patientOrderList} = useSelector((state: RootState) => state.profile)

  const latestOrderId = useMemo(() => {
    const orders = (patientOrderList ?? [])
      .filter((order: any) => Boolean(order.order_id) && order.case_submitted === true)
      .sort(
        (a: any, b: any) =>
          new Date(a.order_creation_date ?? '').getTime() -
          new Date(b.order_creation_date ?? '').getTime()
      )
    return orders.length > 0 ? String(orders[orders.length - 1].order_id) : null
  }, [patientOrderList])

  const finishIntro = () => {
    // Prevent multiple navigations
    if (hasNavigatedRef.current || isNavigating) return
    hasNavigatedRef.current = true
    setIsNavigating(true)

    // Clear timer if it exists
    if (timerRef.current) {
      clearInterval(timerRef.current)
      timerRef.current = null
    }

    const params = new URLSearchParams()
    params.set('loading_data', 'true')
    if (latestOrderId) {
      params.set('order_id', latestOrderId)
    }
    sessionStorage.setItem('treatment_progress_loading', 'true')

    navigate(`/profile/${patientId}/aligner-tracking?${params.toString()}`, {
      replace: true,
    })
  }

  useEffect(() => {
    // Reset navigation flag when component mounts
    hasNavigatedRef.current = false
    setIsNavigating(false)

    // Clear any existing timer
    if (timerRef.current) {
      clearInterval(timerRef.current)
      timerRef.current = null
    }

    const endTime = Date.now() + INTRO_AUTO_NAVIGATE_DELAY_MS

    timerRef.current = setInterval(() => {
      const remaining = Math.ceil((endTime - Date.now()) / 1000)

      if (remaining <= 0) {
        if (timerRef.current) {
          clearInterval(timerRef.current)
          timerRef.current = null
        }
        finishIntro()
      } else {
        setCountdown(remaining)
      }
    }, 1000)

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current)
        timerRef.current = null
      }
    }
  }, [])

  return (
    <BorderedCard>
      <div className='flex flex-col text-center gap-4 items-center justify-center md:p-4'>
        <div className='w-12 h-12 flex justify-center items-center bg-tertiarySupport rounded-full'>
          <CheckedCircleOutlineIcon />
        </div>
        <div>
          <div className='text-lg font-semibold'>Treatment started</div>
          <div className='text-textColor font-normal'>
            The case has been successfully set up and is now Ongoing.
          </div>
        </div>
        <div className='text-center'>
          <button
            onClick={finishIntro}
            disabled={isNavigating}
            className={`bg-primaryColor text-white font-semibold rounded-lg px-6 py-3 mb-2 ${
              isNavigating ? 'opacity-50 cursor-not-allowed' : ''
            }`}
          >
            { 'View treatment progress'}
          </button>
          <div className='text-textColor font-normal'>
            {`You will automatically view progress in ${countdown}s`}
          </div>
        </div>
      </div>
    </BorderedCard>
  )
}

export default TreatmentStartedIntro
